import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import api from "../api";
import SEO from "../components/SEO";
import { useI18n } from "../contexts/I18nContext";
import { useShop } from "../contexts/ShopContext";
import { SearchSuggestionsPanel, useSearchSuggestions } from "../components/SearchSuggestions";
import { getStoreProductTitle } from "../utils/productDisplay";
import { storeCategorySlug } from "../utils/publicNavigation";

/* ── Category definitions with product images ── */
const STORE_CATEGORIES = [
  {
    id: "고속인쇄기", slug: "high-speed-printer", label: "고속 인쇄기", tKey: "home_catDigital",
    img: "/api/category-images/1.고속인쇄기.webp", fallback: "/assets/de/cat-1.webp",
  },
  {
    id: "디자인용복합기", slug: "design-copier", label: "디자인용 복합기", tKey: "home_catDesign",
    img: "/api/category-images/2.복합기.webp", fallback: "/assets/de/cat-2.webp",
  },
  {
    id: "사무용복합기", slug: "office-copier", label: "사무용 복합기", tKey: "home_catOffice",
    img: "/api/category-images/2.복합기.webp", fallback: "/assets/de/cat-2.webp",
  },
  {
    id: "소형복합기", slug: "compact-copier", label: "소형복합기", tKey: "home_catSmall",
    img: "/api/category-images/3.소형복합기.webp", fallback: "/assets/de/cat-3.webp",
  },
  {
    id: "프린터", slug: "printer", label: "프린터", tKey: "home_catPrinter",
    img: "/api/category-images/4.프린터.webp", fallback: "/assets/de/cat-4.webp",
  },
  {
    id: "토너소모품", slug: "toner-supplies", label: "토너·소모품", tKey: "home_catToner",
    img: "/api/category-images/5.토너.webp", fallback: "/assets/de/cat-5.webp",
  },
];

const showCategoryFallback = (event, fallback) => {
  const image = event.currentTarget;
  if (image.dataset.fallbackApplied) return;
  image.dataset.fallbackApplied = "true";
  image.src = fallback;
};

/* ── Slug ↔ ID helpers ── */
const slugToCategory = Object.fromEntries(STORE_CATEGORIES.map(c => [c.slug, c.id]));
const categoryToSlug = Object.fromEntries(STORE_CATEGORIES.map(c => [c.id, c.slug]));

/* ── Horizontal Carousel component ── */
function ProductCarousel({ products, title, categoryId }) {
  const { t } = useI18n();
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);

  const updateScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
    const cardW = el.querySelector("a")?.offsetWidth || 240;
    const gap = 16;
    setCurrentPage(Math.round(el.scrollLeft / (cardW + gap)));
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateScroll, { passive: true });
    updateScroll();
    return () => el.removeEventListener("scroll", updateScroll);
  }, [updateScroll, products]);

  const scroll = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    const cardW = el.querySelector("a")?.offsetWidth || 240;
    el.scrollBy({ left: dir * (cardW + 16) * 3, behavior: "smooth" });
  };

  const totalPages = Math.max(1, Math.ceil(products.length / 3));

  return (
    <div className="max-w-5xl mx-auto px-6 py-10">
      {/* Section title */}
      <div className="flex items-baseline justify-between mb-6">
        <h2 className="text-[24px] md:text-[28px] font-bold tracking-tight text-[#1d1d1f]">
          {title}
        </h2>
        {categoryId && (
          <Link to={`/store/${categoryToSlug[categoryId] || categoryId}`} className="text-[15px] text-[#0071e3] hover:underline shrink-0">
            {t.store_viewAll || '전체보기'} →
          </Link>
        )}
      </div>

      {/* Carousel */}
      <div className="relative group">
        {/* Left arrow */}
        {canScrollLeft && (
          <button
            onClick={() => scroll(-1)}
            className="absolute left-[-20px] top-[40%] -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white/90 shadow-lg border border-[#d2d2d7]/50 items-center justify-center text-[#1d1d1f] opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white hidden md:flex"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" /></svg>
          </button>
        )}

        {/* Right arrow */}
        {canScrollRight && (
          <button
            onClick={() => scroll(1)}
            className="absolute right-[-20px] top-[40%] -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white/90 shadow-lg border border-[#d2d2d7]/50 items-center justify-center text-[#1d1d1f] opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white hidden md:flex"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" /></svg>
          </button>
        )}

        {/* Scrollable cards */}
        <div
          ref={scrollRef}
          className="flex gap-4 overflow-x-auto pb-2"
          style={{ scrollbarWidth: "none", scrollSnapType: "x mandatory", msOverflowStyle: "none", WebkitOverflowScrolling: "touch" }}
        >
          {products.map((p) => {
            const saleType = p.sale_type || "sale";
            const linkTo = `/p/${p.slug}`;
            return (
              <Link
                key={p.id}
                to={linkTo}
                className="shrink-0 w-[260px] md:w-[300px] bg-white rounded-2xl border border-[#e8e8ed] overflow-hidden hover:shadow-lg transition-all duration-300"
                style={{ scrollSnapAlign: "start" }}
              >
                {/* Image */}
                <div className="relative aspect-[4/3] bg-[#fafafa] flex items-center justify-center p-6">
                  <span className={`absolute top-3 left-3 text-[10px] font-semibold px-2 py-0.5 rounded-md ${
                    saleType === "rental" ? "bg-emerald-50 text-emerald-700"
                    : saleType === "both" ? "bg-purple-50 text-purple-700"
                    : "bg-blue-50 text-blue-700"
                  }`}>
                    {saleType === "rental" ? (t.store_saleRental || "렌탈") : saleType === "both" ? (t.store_saleBoth || "판매+렌탈") : (t.store_salePurchase || "판매")}
                  </span>
                  {p.image_url ? (
                    <img
                      src={p.image_url}
                      alt={p.name}
                      className="max-w-full max-h-full object-contain"
                      loading="lazy"
                      onError={(e) => { e.target.style.display = "none"; if (e.target.nextSibling) e.target.nextSibling.style.display = "flex"; }}
                    />
                  ) : null}
                  <div className={`items-center justify-center ${p.image_url ? "hidden" : "flex"} w-full h-full`}>
                    <svg className="w-14 h-14 text-[#d2d2d7]" fill="none" stroke="currentColor" strokeWidth={0.7} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0110.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0l.229 2.523a1.125 1.125 0 01-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0021 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 00-1.913-.247M6.34 18H5.25A2.25 2.25 0 013 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 011.913-.247m10.5 0a48.536 48.536 0 00-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659M18.75 12h.008v.008h-.008V12zm-2.25 0h.008v.008H16.5V12z" />
                    </svg>
                  </div>
                </div>

                {/* Info */}
                <div className="px-5 pb-5 pt-3 text-center">
                  <p className="text-[15px] font-bold text-[#1d1d1f] line-clamp-3 leading-snug min-h-[60px]">
                    {getStoreProductTitle(p)}
                  </p>
                  {p.rental_price > 0 ? (
                    <p className="mt-2 text-[17px] font-bold text-emerald-600">
                      {(t.home_monthlyPrice || '월 {price}원~').replace('{price}', p.rental_price.toLocaleString())}
                    </p>
                  ) : p.price > 0 ? (
                    <p className="mt-2 text-[17px] font-bold text-[#1d1d1f]">
                      ₩{p.price.toLocaleString()}
                    </p>
                  ) : (
                    <p className="mt-2 text-[14px] font-bold text-[#86868b]">{t.home_priceInquiry || '가격문의'}</p>
                  )}
                </div>
              </Link>
            );
          })}
        </div>

        {/* Pagination dots */}
        {totalPages > 1 && (
          <div className="flex justify-center gap-1.5 mt-4">
            {Array.from({ length: Math.min(totalPages, 8) }, (_, i) => (
              <span
                key={i}
                className={`w-[7px] h-[7px] rounded-full transition-colors ${
                  i === currentPage ? "bg-[#1d1d1f]" : "bg-[#d2d2d7]"
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Search results grid ── */
function SearchResultsGrid({ products, query }) {
  const { t } = useI18n();
  const [saleFilter, setSaleFilter] = useState("all");
  useEffect(() => setSaleFilter("all"), [query]);
  const visibleProducts = products.filter(p => saleFilter === "all" || p.sale_type === saleFilter || p.sale_type === "both");
  return (
    <section className="max-w-5xl mx-auto px-6 py-10">
      <h2 className="text-[24px] md:text-[28px] font-bold tracking-tight text-[#1d1d1f] mb-2">
        {t.store_searchResults || '검색 결과'}
      </h2>
      <p className="text-[16px] text-[#86868b] mb-6">
        "{query}" — {products.length}{t.store_productUnit || '개 제품'}
      </p>
      <div className="flex flex-wrap gap-2 mb-6" aria-label="검색 상품 유형">
        {[["all", "전체"], ["sale", "구매"], ["rental", "렌탈"]].map(([value, label]) => (
          <button key={value} type="button" aria-pressed={saleFilter === value} onClick={() => setSaleFilter(value)}
            className={`px-4 py-2 border text-sm ${saleFilter === value ? "bg-[#1d1d1f] text-white" : "bg-white text-gray-700"}`}>
            {label} ({products.filter(p => value === "all" || p.sale_type === value || p.sale_type === "both").length})
          </button>
        ))}
      </div>
      {visibleProducts.length === 0 && <p role="status" className="py-6 text-gray-600">선택한 유형의 검색 결과가 없습니다.</p>}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-5">
        {visibleProducts.map((p) => {
          const saleType = p.sale_type || "sale";
          const linkTo = `/p/${p.slug}`;
          return (
            <Link
              key={p.id}
              to={linkTo}
              className="bg-white rounded-2xl border border-[#e8e8ed] overflow-hidden hover:shadow-lg transition-shadow duration-300"
            >
              <div className="relative aspect-square bg-[#fafafa] flex items-center justify-center p-4">
                <span className={`absolute top-3 left-3 text-[10px] font-semibold px-2 py-0.5 rounded-md ${
                  saleType === "rental" ? "bg-emerald-50 text-emerald-700" : saleType === "both" ? "bg-purple-50 text-purple-700" : "bg-blue-50 text-blue-700"
                }`}>
                  {saleType === "rental" ? (t.store_saleRental || "렌탈") : saleType === "both" ? (t.store_saleBoth || "판매+렌탈") : (t.store_salePurchase || "판매")}
                </span>
                {p.image_url ? (
                  <img src={p.image_url} alt={p.name} className="w-[75%] h-[75%] object-contain" loading="lazy"
                    onError={(e) => { e.target.style.display = "none"; if (e.target.nextSibling) e.target.nextSibling.style.display = "flex"; }} />
                ) : null}
                <div className={`items-center justify-center ${p.image_url ? "hidden" : "flex"} w-full h-full`}>
                  <svg className="w-12 h-12 text-[#d2d2d7]" fill="none" stroke="currentColor" strokeWidth={0.8} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0110.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0l.229 2.523a1.125 1.125 0 01-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0021 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 00-1.913-.247M6.34 18H5.25A2.25 2.25 0 013 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 011.913-.247m10.5 0a48.536 48.536 0 00-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659M18.75 12h.008v.008h-.008V12zm-2.25 0h.008v.008H16.5V12z" />
                  </svg>
                </div>
              </div>
              <div className="px-4 pb-4 pt-1 text-center">
                <p className="text-[14px] font-bold text-[#1d1d1f] line-clamp-3 leading-snug min-h-[54px]">{getStoreProductTitle(p)}</p>
                {p.stock_status === "soldout" && <p className="mt-2 text-sm font-semibold text-red-700">품절 · 재입고 대기</p>}
                {p.rental_price > 0 ? (
                  <p className="mt-1.5 text-[15px] font-bold text-emerald-600">{(t.home_monthlyPrice || '월 {price}원~').replace('{price}', p.rental_price.toLocaleString())}</p>
                ) : p.price > 0 ? (
                  <p className="mt-1.5 text-[16px] font-bold text-[#1d1d1f]">₩{p.price.toLocaleString()}</p>
                ) : (
                  <p className="mt-1.5 text-[13px] font-bold text-[#86868b]">{t.home_priceInquiry || '가격문의'}</p>
                )}
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}

/* ── Sub-category definitions for 토너소모품 ── */
const TONER_SUB_CATEGORIES = [
  { id: "", label: "전체", tKey: "store_subAll" },
  { id: "토너", label: "토너", tKey: "store_subToner" },
  { id: "드럼", label: "드럼", tKey: "store_subDrum" },
  { id: "현상기", label: "현상기", tKey: "store_subDeveloper" },
  { id: "정착기", label: "정착기", tKey: "store_subFuser" },
  { id: "부품", label: "부품", tKey: "store_subParts" },
  { id: "종이", label: "종이", tKey: "store_subPaper" },
];

const TONER_BRAND_FILTERS = [
  { id: "", label: "전체" },
  { id: "hp", label: "HP" },
  { id: "samsung", label: "삼성" },
  { id: "canon", label: "캐논" },
  { id: "brother", label: "브라더" },
  { id: "sindoh", label: "신도리코" },
  { id: "xerox", label: "Xerox" },
  { id: "kyocera", label: "Kyocera" },
  { id: "lexmark", label: "Lexmark" },
];

function getTonerBrandId(product = {}) {
  const text = `${product.brand || ""} ${product.name || ""} ${product.model_name || ""}`.toLowerCase();
  if (/(^|[^a-z])hp([^a-z]|$)|한국hp/.test(text)) return "hp";
  if (/samsung|삼성/.test(text)) return "samsung";
  if (/canon|캐논/.test(text)) return "canon";
  if (/brother|브라더/.test(text)) return "brother";
  if (/sindoh|신도리코/.test(text)) return "sindoh";
  if (/fuji\s*xerox|fujixerox|xerox|후지제록스|제록스/.test(text)) return "xerox";
  if (/kyocera|교세라/.test(text)) return "kyocera";
  if (/lexmark|렉스마크/.test(text)) return "lexmark";
  return "other";
}

/* ── CMYK Color detector from product name / model ── */
const CMYK_MAP = {
  K: { key: "K", label: "검정", color: "#231F20" },
  C: { key: "C", label: "시안", color: "#00AEEF" },
  M: { key: "M", label: "마젠타", color: "#EC008C" },
  Y: { key: "Y", label: "옐로우", color: "#FFD500" },
};
const CMYK_ORDER = ["K", "C", "M", "Y"];

function detectTonerColors(name = "", model = "") {
  const raw = `${name || ""} ${model || ""}`;
  const upper = raw.toUpperCase();
  const found = new Set();
  const add = (key) => { if (CMYK_MAP[key]) found.add(key); };

  if (/폐\s*토너\s*통|폐토너통|WASTE\s*TONER/i.test(raw)) {
    return [];
  }
  if (/4\s*색|4\s*COLOR|CMYK|CLT[-\s]?P\d|4색\s*(SET|세트)/i.test(raw)) {
    return CMYK_ORDER.map(key => CMYK_MAP[key]);
  }
  if (/3\s*색|3\s*COLOR|컬러\s*[:：]?\s*3/i.test(raw)) {
    return ["C", "M", "Y"].map(key => CMYK_MAP[key]);
  }

  if (/검정|블랙|흑백|BLACK|\bBK\b/i.test(raw)) add("K");
  if (/파랑|청록|시안|CYAN|BLUE/i.test(raw)) add("C");
  if (/빨강|마젠타|MAGENTA|RED/i.test(raw)) add("M");
  if (/노랑|옐로우|YELLOW/i.test(raw)) add("Y");

  if (/\bCLT[-\s]?K/i.test(upper) || /\bMLT[-\s]/i.test(upper)) add("K");
  if (/\bCLT[-\s]?C/i.test(upper)) add("C");
  if (/\bCLT[-\s]?M/i.test(upper)) add("M");
  if (/\bCLT[-\s]?Y/i.test(upper)) add("Y");

  const tokens = upper.match(/[A-Z]{1,8}[-\s]?[A-Z0-9]+(?:[-\s]?[A-Z0-9]+)*/g) || [];
  tokens.forEach((token) => {
    const compact = token.replace(/[^A-Z0-9]/g, "");
    if (compact.length < 4) return;
    if (/(BK|KK)$/.test(compact)) add("K");
    if (/(KC)$/.test(compact)) add("C");
    if (/(KM)$/.test(compact)) add("M");
    if (/(KY)$/.test(compact)) add("Y");
    if (/\d+T\d+K$/.test(compact)) add("K");
    if (/^(CRG|NPG|TN|TK)[A-Z0-9]*K$/.test(compact)) add("K");
    if (/^(CRG|NPG|TN|TK)[A-Z0-9]*C$/.test(compact)) add("C");
    if (/^(CRG|NPG|TN|TK)[A-Z0-9]*M$/.test(compact)) add("M");
    if (/^(CRG|NPG|TN|TK)[A-Z0-9]*Y$/.test(compact)) add("Y");
  });

  if (found.size === 0) {
    if (/(^|[^가-힣])컬러($|[^가-힣])|COLOR/i.test(raw)) {
      return ["C", "M", "Y"].map(key => CMYK_MAP[key]);
    }
    const isMonoHint = /\b(MLT|ML|SCX|SF|TN|DR)[-\s]?\d|D\d{2,4}S\b|TK[-\s]?\d{3,5}\b|APEOSPRINT/i.test(upper);
    if (isMonoHint) add("K");
  }
  return CMYK_ORDER.filter(key => found.has(key)).map(key => CMYK_MAP[key]);
}

/* ── Color dot component ── */
function ColorDot({ cmyk }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="w-4 h-4 rounded-full border border-gray-200 shadow-sm shrink-0"
        style={{ backgroundColor: cmyk.color }} />
      <span className="text-[11px] font-medium text-gray-600">{cmyk.key}</span>
    </div>
  );
}

/* ── Horizontal Toner/Supplies Card (Apple Modern Style) ── */
function TonerCard({ p }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const saleType = p.sale_type || "sale";
  const specs = (p.specs_json && typeof p.specs_json === "object" && !Array.isArray(p.specs_json)) ? p.specs_json : {};
  const tonerColors = detectTonerColors(p.name, p.model_name);
  const specEntries = Object.entries(specs)
    .filter(([, v]) => typeof v === "string" || typeof v === "number")
    .slice(0, 4);

  const handleInquiry = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const prodName = p.product_code ? `[${p.product_code}] ${getStoreProductTitle(p)}` : getStoreProductTitle(p);
    navigate("/consultation", { state: { product: { id: p.id, name: prodName, image_url: p.image_url, rental_price: p.rental_price, price: p.price, product_code: p.product_code } } });
  };

  return (
    <div className="dept-catalog-card bg-white rounded-3xl shadow-[0_1px_3px_rgba(0,0,0,0.06),0_4px_16px_rgba(0,0,0,0.04)] hover:shadow-[0_4px_24px_rgba(0,0,0,0.1),0_8px_40px_rgba(0,0,0,0.06)] transition-all duration-500 ease-out overflow-hidden group/card">
      <div className="flex flex-col sm:flex-row">
        {/* Left: Image + Toner/Color Info */}
        <Link to={`/p/${p.slug}`} className="sm:w-60 shrink-0 flex flex-row items-stretch relative">
          {/* Sale type badge */}
          <span className={`absolute top-3 left-3 z-10 text-[10px] font-semibold px-2.5 py-1 rounded-full backdrop-blur-md ${
            saleType === "rental" ? "bg-emerald-500/10 text-emerald-600" : saleType === "both" ? "bg-purple-500/10 text-purple-600" : "bg-[#0071e3]/10 text-[#0071e3]"
          }`}>
            {saleType === "rental" ? (t.store_saleRental || "렌탈") : saleType === "both" ? (t.store_saleBoth || "판매+렌탈") : (t.store_salePurchase || "판매")}
          </span>
          {/* 품절 badge (컴퓨존 재입고대기) */}
          {p.stock_status === "soldout" && (
            <span className="absolute top-3 right-3 z-10 text-[10px] font-bold px-2.5 py-1 rounded-full bg-[#1d1d1f] text-white backdrop-blur-md">
              품절
            </span>
          )}

          {/* Image cell */}
          <div className="w-36 shrink-0 bg-gradient-to-br from-[#fbfbfd] to-[#f5f5f7] flex items-center justify-center p-5">
            {p.image_url ? (
              <img src={p.image_url} alt={p.name} className="w-24 h-24 object-contain group-hover/card:scale-105 transition-transform duration-500" loading="lazy"
                onError={e => { e.target.style.display = "none"; }} />
            ) : (
              <div className="w-24 h-24 flex items-center justify-center">
                <svg className="w-12 h-12 text-[#d2d2d7]" fill="none" stroke="currentColor" strokeWidth={0.7} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0110.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0l.229 2.523a1.125 1.125 0 01-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0021 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 00-1.913-.247M6.34 18H5.25A2.25 2.25 0 013 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 011.913-.247m10.5 0a48.536 48.536 0 00-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659" />
                </svg>
              </div>
            )}
          </div>

          {/* Color cell — toner: CMYK dots / machine: 칼라·흑백 badge */}
          {p.color_type ? (
          <div className="flex flex-col justify-center items-center gap-2.5 px-4 py-4 min-w-[5.5rem]">
            <span className={`text-[11px] font-bold px-3 py-1.5 rounded-full ${
              p.color_type === 'color'
                ? 'bg-gradient-to-r from-cyan-50 to-pink-50 text-indigo-600 ring-1 ring-indigo-200'
                : 'bg-gray-100 text-gray-600 ring-1 ring-gray-200'
            }`}>
              {p.color_type === 'color' ? '칼라' : '흑백'}
            </span>
            {p.compat_count > 0 && (
              <span className="text-[9px] text-[#0071e3] font-medium">호환 토너 {p.compat_count}</span>
            )}
          </div>
          ) : (p.store_category === "토너소모품" || p.sub_category === "토너") && tonerColors.length > 0 ? (
          <div className="flex flex-col justify-center items-center gap-2.5 px-4 py-4 min-w-[5.5rem]">
            <span className="text-[10px] font-semibold text-center px-2.5 py-1 rounded-full bg-[#f5f5f7] text-[#86868b] tracking-wide">
              색상
            </span>
            <div className="flex flex-col items-center gap-1.5">
              {tonerColors.map(c => (
                <div key={c.key} className="flex items-center gap-1.5">
                  <span className="w-5 h-5 rounded-full shadow-[inset_0_1px_2px_rgba(0,0,0,0.1),0_1px_3px_rgba(0,0,0,0.12)] shrink-0"
                    style={{ backgroundColor: c.color }} />
                  <span className="text-[10px] font-bold tracking-wide text-[#6e6e73]">
                    {c.key}
                  </span>
                </div>
              ))}
            </div>
            {p.compat_count > 0 && (
              <span className="text-[9px] text-[#0071e3] font-medium mt-1">호환 기종 {p.compat_count}</span>
            )}
          </div>
          ) : null}
        </Link>

        {/* Middle: Info + Specs */}
        <div className="flex-1 px-5 md:px-7 py-4 md:py-5 min-w-0 flex flex-col justify-center">
          <Link to={`/p/${p.slug}`} className="block group">
            <p className="text-[15px] md:text-[17px] font-semibold text-[#1d1d1f] group-hover:text-[#0071e3] transition-colors duration-300 line-clamp-3 leading-snug tracking-tight">
              {p.product_code && <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 mr-1.5 align-middle"><svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z" /><path strokeLinecap="round" strokeLinejoin="round" d="M6 6h.008v.008H6V6z" /></svg>{p.product_code}</span>}
              {getStoreProductTitle(p)}
            </p>
          </Link>
          <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
            {p.brand && <span className="text-[12px] text-[#86868b] font-medium">{p.brand}</span>}
            {p.category && <span className="text-[12px] text-[#aeaeb2]">·</span>}
            {p.category && <span className="text-[12px] text-[#86868b]">{p.category}</span>}
            {p.sub_category && <span className="text-[10px] bg-[#f5f5f7] text-[#6e6e73] px-2 py-0.5 rounded-full font-medium">{p.sub_category}</span>}
          </div>
          {/* 소모품(토너) 카드는 호환 기종을 우선 노출 — 고객이 가장 알고 싶어하는
              정보(어느 프린터에 맞는지)가 generic AI 마케팅 텍스트보다 가치 있음. */}
          {(() => {
            const isConsumable = (p.store_category || "") === "토너소모품";
            const compatModels = (p.compatible_models || "").trim();
            if (isConsumable && compatModels) {
              return (
                <p className="text-[12px] md:text-[13px] text-[#3a3a3c] mt-2 md:mt-3 line-clamp-2 leading-relaxed">
                  <span className="font-semibold">✓ 호환 기종</span>
                  <span className="mx-1.5 text-[#aeaeb2]">·</span>
                  <span className="text-[#6e6e73]">{compatModels}</span>
                </p>
              );
            }
            if (p.subtitle || p.seo_description) {
              return (
                <p className="text-[12px] md:text-[13px] text-[#86868b] mt-2 md:mt-3 line-clamp-2 leading-relaxed font-light">{p.subtitle || p.seo_description}</p>
              );
            }
            return null;
          })()}
          {/* Specs preview - hide on mobile */}
          {specEntries.length > 0 && (
            <div className="mt-3 hidden sm:flex flex-wrap gap-x-5 gap-y-1.5">
              {specEntries.map(([k, v]) => (
                <span key={k} className="text-[11px] text-[#86868b]">
                  <span className="font-medium text-[#3a3a3c]">{k}</span> {v}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Right: Price + Action Buttons */}
        <div className="dept-catalog-actions sm:w-48 shrink-0 px-5 md:px-7 py-4 md:py-5 flex flex-row sm:flex-col items-center sm:items-end justify-between sm:justify-center gap-4">
          {p.price > 0 ? (
            <div className="text-right">
              <p className="text-[22px] font-bold text-[#1d1d1f] tracking-tight"><span className="text-[16px] font-normal text-[#86868b]">₩</span>{p.price.toLocaleString()}</p>
            </div>
          ) : p.rental_price > 0 ? (
            <div className="text-right">
              <p className="text-[18px] font-bold text-emerald-600 tracking-tight">{(t.home_monthlyPrice || '월 {price}원~').replace('{price}', p.rental_price.toLocaleString()).replace('~', '')}</p>
            </div>
          ) : (
            <p className="text-[14px] font-medium text-[#aeaeb2] italic">{t.home_priceInquiry || '가격문의'}</p>
          )}

          <div className="flex flex-col gap-2 w-full mt-1">
            {p.price > 0 ? (
              <Link to={`/p/${p.slug}`}
                className="w-full px-5 py-2.5 bg-[#0071e3] text-white text-[13px] font-semibold rounded-full hover:bg-[#0077ed] active:scale-[0.97] transition-all duration-200 shadow-[0_1px_4px_rgba(0,113,227,0.3)] text-center">
                구매하기
              </Link>
            ) : (
              <button onClick={handleInquiry}
                className="w-full px-5 py-2.5 bg-[#0071e3] text-white text-[13px] font-semibold rounded-full hover:bg-[#0077ed] active:scale-[0.97] transition-all duration-200 shadow-[0_1px_4px_rgba(0,113,227,0.3)]">
                {t.store_inquiry || '문의하기'}
              </button>
            )}
            <Link to={`/p/${p.slug}`}
              className="w-full px-5 py-2.5 text-[#0071e3] text-[13px] font-semibold rounded-full hover:bg-[#0071e3]/5 active:scale-[0.97] transition-all duration-200 text-center">
              {t.store_details || '상세보기'}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Square Product Card (Apple Modern Style) ── */
function SquareCard({ p }) {
  const { t } = useI18n();
  const saleType = p.sale_type || "sale";
  return (
    <Link key={p.id} to={`/p/${p.slug}`}
      className="bg-white rounded-3xl shadow-[0_1px_3px_rgba(0,0,0,0.06),0_4px_16px_rgba(0,0,0,0.04)] overflow-hidden hover:shadow-[0_4px_24px_rgba(0,0,0,0.1),0_8px_40px_rgba(0,0,0,0.06)] hover:-translate-y-1 transition-all duration-500 ease-out group">
      <div className="relative aspect-square bg-gradient-to-br from-[#fbfbfd] to-[#f5f5f7] flex items-center justify-center p-6">
        <span className={`absolute top-3 left-3 text-[10px] font-semibold px-2.5 py-1 rounded-full backdrop-blur-md ${
          saleType === "rental" ? "bg-emerald-500/10 text-emerald-600" : saleType === "both" ? "bg-purple-500/10 text-purple-600" : "bg-[#0071e3]/10 text-[#0071e3]"
        }`}>
          {saleType === "rental" ? (t.store_saleRental || "렌탈") : saleType === "both" ? (t.store_saleBoth || "판매+렌탈") : (t.store_salePurchase || "판매")}
        </span>
        {p.image_url ? (
          <img src={p.image_url} alt={p.name} className="w-[70%] h-[70%] object-contain group-hover:scale-105 transition-transform duration-500" loading="lazy"
            onError={e => { e.target.style.display = "none"; if (e.target.nextSibling) e.target.nextSibling.style.display = "flex"; }} />
        ) : null}
        <div className={`items-center justify-center ${p.image_url ? "hidden" : "flex"} w-full h-full`}>
          <svg className="w-14 h-14 text-[#d2d2d7]" fill="none" stroke="currentColor" strokeWidth={0.7} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0110.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0l.229 2.523a1.125 1.125 0 01-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0021 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 00-1.913-.247M6.34 18H5.25A2.25 2.25 0 013 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 011.913-.247m10.5 0a48.536 48.536 0 00-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659M18.75 12h.008v.008h-.008V12zm-2.25 0h.008v.008H16.5V12z" />
          </svg>
        </div>
      </div>
      <div className="px-5 pb-5 pt-3 text-center">
        <p className="text-[14px] font-semibold text-[#1d1d1f] line-clamp-3 leading-snug min-h-[54px] tracking-tight">{getStoreProductTitle(p)}</p>
        {p.brand && <p className="text-[12px] text-[#86868b] font-medium mt-1">{p.brand}</p>}
        {p.rental_price > 0 ? (
          <p className="mt-2 text-[15px] font-bold text-emerald-600 tracking-tight">{(t.home_monthlyPrice || '월 {price}원~').replace('{price}', p.rental_price.toLocaleString())}</p>
        ) : p.price > 0 ? (
          <p className="mt-2 text-[17px] font-bold text-[#1d1d1f] tracking-tight"><span className="text-[13px] font-normal text-[#86868b]">₩</span>{p.price.toLocaleString()}</p>
        ) : (
          <p className="mt-2 text-[13px] font-medium text-[#aeaeb2] italic">{t.home_priceInquiry || '가격문의'}</p>
        )}
      </div>
    </Link>
  );
}

/* ── Category Full Page View ── */
function CategoryFullPage({ products, category, allCategories, loading }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { addToCart, fetchCart } = useShop();
  const cat = allCategories.find(c => c.id === category);
  const label = t[cat?.tKey] || cat?.label || category;
  const [sortBy, setSortBy] = useState("latest");
  const [subCat, setSubCat] = useState("");
  const [tonerBrand, setTonerBrand] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [actionToast, setActionToast] = useState("");

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selected.size === sorted.length) setSelected(new Set());
    else setSelected(new Set(sorted.map(p => p.id)));
  };

  const handleBulkCart = async ({ checkout = false } = {}) => {
    let ok = 0;
    let firstError = "";
    for (const id of selected) {
      const res = await addToCart("page", id, 1);
      if (res?.needLogin) { navigate("/login", { state: { from: window.location.pathname + window.location.search } }); return; }
      if (!res?.error) ok++;
      else if (!firstError) firstError = res.error;
    }
    await fetchCart?.();
    setSelected(new Set());
    // 실패를 삼키지 않는다 — 서버 지연/오류면 사용자에게 그 사유를 그대로 보여준다.
    setActionToast(
      ok > 0
        ? `${ok}개 제품이 장바구니에 추가되었습니다${firstError ? ` (일부 실패: ${firstError})` : ""}`
        : `장바구니 추가 실패: ${firstError || "오류가 발생했습니다"}`
    );
    setTimeout(() => setActionToast(""), 4000);
    if (checkout && ok > 0) navigate("/checkout");
  };

  const tonerSubFilteredProducts = useMemo(() => {
    let arr = products;
    if (category === "토너소모품" && subCat) arr = arr.filter(p => p.sub_category === subCat);
    return arr;
  }, [products, category, subCat]);

  const tonerBrandCounts = useMemo(() => {
    if (category !== "토너소모품") return {};
    const counts = { "": tonerSubFilteredProducts.length };
    for (const p of tonerSubFilteredProducts) {
      const brandId = getTonerBrandId(p);
      counts[brandId] = (counts[brandId] || 0) + 1;
    }
    return counts;
  }, [tonerSubFilteredProducts, category]);

  const availableTonerBrandFilters = useMemo(() => {
    if (category !== "토너소모품") return [];
    const filters = TONER_BRAND_FILTERS.filter(b => !b.id || (tonerBrandCounts[b.id] || 0) > 0);
    if ((tonerBrandCounts.other || 0) > 0) filters.push({ id: "other", label: "기타" });
    return filters;
  }, [category, tonerBrandCounts]);

  useEffect(() => {
    if (category === "토너소모품" && tonerBrand && !availableTonerBrandFilters.some(b => b.id === tonerBrand)) {
      setTonerBrand("");
    }
  }, [category, tonerBrand, availableTonerBrandFilters]);

  useEffect(() => {
    setSelected(new Set());
  }, [subCat, tonerBrand]);

  const filtered = useMemo(() => {
    let arr = tonerSubFilteredProducts;
    if (category === "토너소모품" && tonerBrand) arr = arr.filter(p => getTonerBrandId(p) === tonerBrand);
    return arr;
  }, [tonerSubFilteredProducts, category, tonerBrand]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    if (sortBy === "latest") {
      arr.sort((a, b) => {
        const aOrder = Number((category === "토너소모품" && subCat ? a.sub_display_order : a.display_order) || 0);
        const bOrder = Number((category === "토너소모품" && subCat ? b.sub_display_order : b.display_order) || 0);
        if (aOrder > 0 && bOrder > 0) return aOrder - bOrder;
        if (aOrder > 0) return -1;
        if (bOrder > 0) return 1;
        return 0;
      });
    }
    else if (sortBy === "price_asc") arr.sort((a, b) => (a.price || 0) - (b.price || 0));
    else if (sortBy === "price_desc") arr.sort((a, b) => (b.price || 0) - (a.price || 0));
    else if (sortBy === "rental") arr.sort((a, b) => (a.rental_price || 0) - (b.rental_price || 0));
    else if (sortBy === "name") arr.sort((a, b) => getStoreProductTitle(a).localeCompare(getStoreProductTitle(b)));
    return arr;
  }, [filtered, sortBy, category, subCat]);

  /* Sub-category counts for badge */
  const subCatCounts = useMemo(() => {
    if (category !== "토너소모품") return {};
    const counts = {};
    for (const p of products) {
      const sc = p.sub_category || "";
      counts[sc] = (counts[sc] || 0) + 1;
    }
    return counts;
  }, [products, category]);

  const activeTonerBrandLabel = category === "토너소모품" && tonerBrand
    ? availableTonerBrandFilters.find(b => b.id === tonerBrand)?.label
    : "";

  return (
    <div className={`dept-store-page ${selected.size ? "has-selection" : ""} bg-white min-h-screen`}>
      <SEO
        title={`${label} | 복사기 복합기 스토어 - 일광오피스`}
        description={`${label} 제품 ${products.length}개 - 복사기 복합기 판매 가격비교. 렌탈·구매 최저가 비교, 전국 무상 유지보수.`}
        keywords={`${label}, 복사기 ${label}, 복합기 ${label}, ${label} 가격, ${label} 렌탈`}
        path={`/store/${categoryToSlug[category] || category}`}
      />

      {/* ── Editorial category hero ── */}
      <section
        className="dept-store-hero"
        style={{
          position: "relative",
          background: "var(--ink-1000)",
          color: "white",
          minHeight: 320,
          display: "flex",
          alignItems: "center",
          overflow: "hidden",
          marginTop: -56,
          paddingTop: 56,
        }}
      >
        <div
          className="dept-store-hero-decoration"
          style={{
            position: "absolute",
            inset: 0,
            background:
              "radial-gradient(900px 500px at 80% 0%, rgba(0,113,227,0.18), transparent 60%), radial-gradient(700px 400px at 10% 100%, rgba(124,58,237,0.12), transparent 60%)",
            pointerEvents: "none",
          }}
        />
        <div
          className="dept-store-hero__content mob-pad-tight"
          style={{
            position: "relative",
            maxWidth: 1240,
            margin: "0 auto",
            padding: "96px 40px 56px",
            width: "100%",
          }}
        >
          <Link
            to="/store"
            style={{
              fontSize: 12,
              color: "rgba(255,255,255,0.5)",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              display: "inline-block",
              marginBottom: 16,
            }}
          >
            ← {t.store_heroTitle || '스토어'}
          </Link>
          <h1
            style={{
              fontSize: "clamp(36px, 6vw, 72px)",
              lineHeight: 1.0,
              letterSpacing: "-0.035em",
              fontWeight: 700,
              margin: 0,
              color: "white",
            }}
          >
            <span
              style={{
                fontFamily: "var(--font-serif)",
                fontStyle: "italic",
                fontWeight: 400,
                background: "linear-gradient(135deg, #fff 0%, #6BB1F5 70%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              {label}
            </span>
          </h1>
          <p
            style={{
              marginTop: 12,
              fontSize: 15,
              color: "rgba(255,255,255,0.6)",
            }}
          >
            {loading ? "…" : sorted.length}{t.store_productUnit || '개 제품'}{subCat && ` · ${subCat}`}{activeTonerBrandLabel && ` · ${activeTonerBrandLabel}`}
          </p>
        </div>
      </section>

      {/* Category tabs with real product images - below banner */}
      <section className="bg-[#fbfbfd] border-b border-[#f0f0f0]">
        <div className="max-w-5xl mx-auto px-4 md:px-6 py-5 md:py-8">
          <div className="flex gap-4 md:gap-8 overflow-x-auto pt-2 pb-2 md:justify-center" style={{ scrollbarWidth: "none" }}>
            {allCategories.map(c => (
              <Link key={c.id} to={`/store/${c.slug}`}
                className={`shrink-0 flex flex-col items-center gap-2 md:gap-2.5 group w-[72px] md:w-[110px] transition-all duration-300 ${
                  c.id === category ? "opacity-100" : "opacity-50 hover:opacity-100"
                }`}>
                <div className={`w-[60px] h-[60px] md:w-[92px] md:h-[92px] rounded-2xl md:rounded-[22px] flex items-center justify-center transition-all duration-300 group-hover:scale-105 ${
                  c.id === category ? "bg-white shadow-[0_2px_12px_rgba(0,113,227,0.15)] ring-[1.5px] ring-[#0071e3]/40" : "bg-white/60"
                }`}>
                  <img src={c.img} alt={t[c.tKey] || c.label} className="w-[42px] h-[42px] md:w-[66px] md:h-[66px] object-contain" loading="lazy" onError={(event) => showCategoryFallback(event, c.fallback)} />
                </div>
                <span className={`text-[11px] md:text-[13px] text-center leading-tight whitespace-nowrap transition-colors ${
                  c.id === category ? "text-[#0071e3] font-semibold" : "text-[#86868b] font-medium"
                }`}>{t[c.tKey] || c.label}</span>
              </Link>
            ))}
          </div>

          {/* Sub-category tabs for 토너소모품 */}
          {category === "토너소모품" && (
            <div className="flex gap-2 mt-4 md:mt-5 overflow-x-auto pb-1 md:justify-center" style={{ scrollbarWidth: "none" }}>
              {TONER_SUB_CATEGORIES.map(sc => {
                const count = sc.id ? (subCatCounts[sc.id] || 0) : products.length;
                return (
                  <button key={sc.id} onClick={() => setSubCat(sc.id)}
                    className={`shrink-0 px-4 md:px-4.5 py-2 rounded-full text-[11px] md:text-[12px] font-medium transition-all duration-300 flex items-center gap-1.5 ${
                      sc.id === subCat
                        ? "bg-[#1d1d1f] text-white shadow-md"
                        : "bg-white text-[#6e6e73] hover:bg-[#f5f5f7] shadow-[0_0_0_1px_rgba(0,0,0,0.06)]"
                    }`}>
                    {t[sc.tKey] || sc.label}
                    <span className={`text-[10px] font-normal ${sc.id === subCat ? "text-white/50" : "text-[#aeaeb2]"}`}>
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Brand chips for 토너소모품 */}
          {category === "토너소모품" && availableTonerBrandFilters.length > 1 && (
            <div className="mt-3 md:mt-4 flex items-center gap-2 overflow-x-auto pb-1 md:justify-center" style={{ scrollbarWidth: "none" }}>
              <span className="shrink-0 text-[11px] md:text-[12px] font-semibold text-[#86868b] px-1">브랜드</span>
              {availableTonerBrandFilters.map(brand => {
                const count = tonerBrandCounts[brand.id] || 0;
                return (
                  <button key={brand.id} onClick={() => setTonerBrand(brand.id)}
                    className={`shrink-0 px-4 py-2 rounded-full text-[11px] md:text-[12px] font-semibold transition-all duration-300 flex items-center gap-1.5 ${
                      brand.id === tonerBrand
                        ? "bg-[#0071e3] text-white shadow-[0_4px_14px_rgba(0,113,227,0.25)]"
                        : "bg-white text-[#3a3a3c] hover:bg-[#f5f5f7] shadow-[0_0_0_1px_rgba(0,0,0,0.06)]"
                    }`}>
                    {brand.label}
                    <span className={`text-[10px] font-normal ${brand.id === tonerBrand ? "text-white/60" : "text-[#aeaeb2]"}`}>
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Sort */}
          <div className="flex justify-end mt-3 md:mt-4">
            <select value={sortBy} onChange={e => setSortBy(e.target.value)}
              className="text-[12px] md:text-[13px] rounded-full px-4 md:px-5 py-2 bg-white text-[#1d1d1f] focus:outline-none focus:ring-2 focus:ring-[#0071e3]/20 shadow-[0_0_0_1px_rgba(0,0,0,0.06)] appearance-none cursor-pointer">
              <option value="latest">{t.store_sortLatest || '추천·진열순'}</option>
              <option value="name">{t.store_sortName || '이름순'}</option>
              <option value="price_asc">{t.store_sortPriceAsc || '가격 낮은순'}</option>
              <option value="price_desc">{t.store_sortPriceDesc || '가격 높은순'}</option>
              <option value="rental">{t.store_sortRental || '렌탈료 낮은순'}</option>
            </select>
          </div>
        </div>
      </section>

      {/* ── Promo Banners (렌탈 / B2B) ── */}
      <section className="max-w-5xl mx-auto px-6 py-6 md:py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Link to="/rental" className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-600 via-violet-600 to-purple-700 p-6 md:p-8 group hover:shadow-xl transition-all duration-500">
            <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_70%_30%,rgba(255,255,255,0.2),transparent_60%)]" />
            <div className="relative z-10">
              <span className="inline-block text-[10px] font-bold tracking-widest text-white/70 uppercase mb-2">RENTAL</span>
              <h3 className="text-xl md:text-2xl font-bold text-white tracking-tight mb-1">전국 렌탈</h3>
              <p className="text-[13px] text-white/80 leading-relaxed mb-4">월 렌탈료로 복합기를 부담 없이.<br className="hidden md:block" />전국 어디서나 설치·유지보수까지.</p>
              <span className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-white group-hover:gap-2.5 transition-all duration-300">
                렌탈 상담받기
                <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" /></svg>
              </span>
            </div>
          </Link>
          <Link to="/b2b" className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-700 p-6 md:p-8 group hover:shadow-xl transition-all duration-500">
            <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_30%_70%,rgba(255,255,255,0.2),transparent_60%)]" />
            <div className="relative z-10">
              <span className="inline-block text-[10px] font-bold tracking-widest text-white/70 uppercase mb-2">B2B</span>
              <h3 className="text-xl md:text-2xl font-bold text-white tracking-tight mb-1">사업자몰</h3>
              <p className="text-[13px] text-white/80 leading-relaxed mb-4">사업자 전용 특가 혜택.<br className="hidden md:block" />대량 구매 할인 · 세금계산서 발행.</p>
              <span className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-white group-hover:gap-2.5 transition-all duration-300">
                사업자몰 바로가기
                <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" /></svg>
              </span>
            </div>
          </Link>
        </div>
      </section>

      {/* Product Grid */}
      <section className="max-w-5xl mx-auto px-4 md:px-6 py-8 md:py-12">
        {loading && (
          <div className="text-center py-24">
            <div className="w-8 h-8 border-2 border-[#0071e3] border-t-transparent rounded-full animate-spin mx-auto" />
            <p className="text-[15px] text-[#86868b] mt-4">{t.store_loading || '제품을 불러오는 중...'}</p>
          </div>
        )}
        {/* Select all toggle */}
        {!loading && sorted.length > 0 && (
          <div className="flex items-center justify-end mb-6">
            <button onClick={selectAll} className="text-[13px] font-medium text-[#0071e3] px-4 py-2 rounded-full hover:bg-[#0071e3]/5 transition-colors">
              {selected.size === sorted.length ? "전체 해제" : "전체 선택"}
            </button>
          </div>
        )}

        {/* All categories use horizontal card layout */}
        <div className="space-y-4">
          {!loading && sorted.map(p => (
            <div key={p.id} className="dept-catalog-row flex items-center gap-3">
              <label
                className={`shrink-0 w-12 h-12 rounded-2xl flex items-center justify-center cursor-pointer transition-all duration-300 ${
                  selected.has(p.id)
                    ? "bg-[#e8f2ff] ring-2 ring-[#0071e3]/25 shadow-[0_4px_18px_rgba(0,113,227,0.18)]"
                    : "bg-white ring-1 ring-[#d2d2d7] hover:ring-[#0071e3]/55 hover:bg-[#f5f9ff]"
                }`}
                title={`${getStoreProductTitle(p)} 선택`}
                aria-label={`${getStoreProductTitle(p)} 선택`}
              >
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={selected.has(p.id)}
                  onChange={() => toggleSelect(p.id)}
                />
                <span
                  className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all duration-200 ${
                    selected.has(p.id)
                      ? "bg-[#0071e3] border-[#0071e3]"
                      : "bg-white border-[#8e8e93]"
                  }`}
                >
                  {selected.has(p.id) && (
                    <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  )}
                </span>
              </label>
              <div className="flex-1 min-w-0">
                <TonerCard p={p} />
              </div>
            </div>
          ))}
        </div>
        {!loading && sorted.length === 0 && (
          <div className="text-center py-24">
            <p className="text-[17px] text-[#aeaeb2] font-light">{t.store_noProducts || '이 카테고리에 제품이 없습니다.'}</p>
          </div>
        )}
      </section>

      {/* Floating action bar — Apple frosted glass style */}
      {selected.size > 0 && (
        <div className="dept-selection-bar fixed bottom-5 md:bottom-8 left-1/2 -translate-x-1/2 z-50 bg-white/80 backdrop-blur-2xl text-[#1d1d1f] rounded-[22px] shadow-[0_8px_40px_rgba(0,0,0,0.12),0_2px_8px_rgba(0,0,0,0.06)] ring-1 ring-black/[0.04] px-4 md:px-6 py-3.5 flex items-center gap-3 md:gap-4 max-w-[95vw] animate-[slideUp_0.3s_ease-out]">
          <span className="dept-selection-count text-[14px] md:text-[15px] font-bold shrink-0 bg-[#0071e3] text-white w-8 h-8 rounded-full flex items-center justify-center text-[13px]">{selected.size}<span className="dept-selection-count-label">개 선택</span></span>
          <div className="w-px h-6 bg-[#d2d2d7]/50" />
          <button onClick={() => handleBulkCart({ checkout: true })}
            className="flex items-center gap-1.5 md:gap-2 px-4 md:px-5 py-2.5 bg-[#0071e3] text-white rounded-full text-[12px] md:text-[13px] font-semibold hover:bg-[#0077ed] active:scale-[0.97] transition-all duration-200 shadow-[0_2px_8px_rgba(0,113,227,0.25)] shrink-0">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 00-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 00-16.536-1.84M7.5 14.25L5.106 5.272M6 20.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm12.75 0a.75.75 0 11-1.5 0 .75.75 0 011.5 0z" /></svg>
            선택 상품 구매
          </button>
          <button onClick={() => handleBulkCart()}
            className="flex items-center gap-1.5 md:gap-2 px-4 md:px-5 py-2.5 rounded-full text-[12px] md:text-[13px] font-semibold hover:bg-[#f5f5f7] active:scale-[0.97] transition-all duration-200 shrink-0">
            장바구니 담기
          </button>
          <button aria-label="상품 선택 해제" onClick={() => { setSelected(new Set()); }}
            className="ml-0.5 md:ml-1 p-2 rounded-full hover:bg-[#f5f5f7] transition-colors">
            <svg className="w-4 h-4 text-[#86868b]" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
      )}

      {/* Toast notification — frosted glass */}
      {actionToast && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 bg-white/80 backdrop-blur-2xl text-[#1d1d1f] px-6 py-3.5 rounded-full shadow-[0_4px_24px_rgba(0,0,0,0.1)] ring-1 ring-black/[0.04] text-[14px] font-medium animate-[slideDown_0.3s_ease-out]">
          <span className="text-[#34c759] mr-1.5">✓</span> {actionToast}
        </div>
      )}
    </div>
  );
}

/* ── Main StorePage ── */
export default function StorePage() {
  const { t } = useI18n();
  const { category: urlCategory } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlSearchQuery = searchParams.get("q") || "";
  const [browseProducts, setBrowseProducts] = useState([]);
  const [categoryCounts, setCategoryCounts] = useState({});
  const [categoryProducts, setCategoryProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchInput, setSearchInput] = useState(urlSearchQuery);
  const [searchQuery, setSearchQuery] = useState(urlSearchQuery);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [suggestActiveIndex, setSuggestActiveIndex] = useState(-1);
  const storeSearchRef = useRef(null);
  const searchSuggestions = useSearchSuggestions(searchInput, suggestOpen, { includeRental: true, limit: 8 });

  // URL이 카테고리 페이지인지 (slug 또는 legacy 한글 id)
  const categoryParam = storeCategorySlug(urlCategory || searchParams.get("분류") || "");
  const resolvedCategory = slugToCategory[categoryParam] || categoryParam;
  const isCategoryView = Boolean(resolvedCategory && STORE_CATEGORIES.some((c) => c.id === resolvedCategory));

  // 브라우즈 모드: 카테고리별 미리보기 16개 + 전체 카운트만 (전체 5,600여 개 전량 로드 금지)
  useEffect(() => {
    if (isCategoryView) return undefined;
    let cancelled = false;
    setLoading(true);
    api.get("/api/store/products", { params: { fields: "list", per_category: 16 } })
      .then((res) => {
        if (cancelled) return;
        setBrowseProducts(res.data.products || []);
        setCategoryCounts(res.data.category_counts || {});
      })
      .catch(() => {
        if (!cancelled) setBrowseProducts([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [isCategoryView]);

  // 카테고리 페이지: 해당 카테고리 상품만
  useEffect(() => {
    if (!isCategoryView) return undefined;
    let cancelled = false;
    setLoading(true);
    setCategoryProducts([]);
    api.get("/api/store/products", { params: { fields: "list", store_category: resolvedCategory } })
      .then((res) => {
        if (!cancelled) setCategoryProducts(res.data.products || []);
      })
      .catch(() => {
        if (!cancelled) setCategoryProducts([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [isCategoryView, resolvedCategory]);

  // 검색: 서버 필터(q=) — 백엔드 _store_product_matches_query가 클라이언트와 동일 기준
  useEffect(() => {
    const q = searchQuery.trim();
    if (!q) {
      setSearchResults(null);
      setSearchLoading(false);
      return undefined;
    }
    let cancelled = false;
    setSearchLoading(true);
    api.get("/api/store/products", { params: { fields: "list", q, include_rental: true, representative: true } })
      .then((res) => {
        if (!cancelled) setSearchResults(res.data.products || []);
      })
      .catch(() => {
        if (!cancelled) setSearchResults([]);
      })
      .finally(() => {
        if (!cancelled) setSearchLoading(false);
      });
    return () => { cancelled = true; };
  }, [searchQuery]);

  useEffect(() => {
    setSearchInput(urlSearchQuery);
    setSearchQuery(urlSearchQuery);
  }, [urlSearchQuery]);

  useEffect(() => {
    const handler = (e) => {
      if (storeSearchRef.current && !storeSearchRef.current.contains(e.target)) {
        setSuggestOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      const q = searchInput.trim();
      setSearchQuery(q);
      if (q !== urlSearchQuery) {
        setSearchParams(q ? { q } : {}, { replace: true });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, setSearchParams, urlSearchQuery]);

  useEffect(() => {
    setSuggestActiveIndex(-1);
  }, [searchInput]);

  // Products grouped by store_category (sale/both only, no rental-only)
  const grouped = useMemo(() => {
    const map = {};
    for (const cat of STORE_CATEGORIES) {
      map[cat.id] = [];
    }
    for (const p of browseProducts) {
      const st = p.sale_type || "sale";
      if (st === "rental") continue; // rental-only products go to 전국렌탈
      const sc = p.store_category || "기타";
      if (map[sc]) map[sc].push(p);
    }
    return map;
  }, [browseProducts]);

  const isSearching = searchQuery.length > 0;

  const handleCategoryClick = (catId) => {
    navigate(`/store/${categoryToSlug[catId] || catId}`);
  };

  const submitStoreSearch = (value = searchInput) => {
    const q = value.trim();
    setSuggestOpen(false);
    setSuggestActiveIndex(-1);
    setSearchInput(q);
    setSearchQuery(q);
    setSearchParams(q ? { q } : {}, { replace: true });
  };

  const pickStoreProduct = (product) => {
    if (product?.slug) navigate(`/p/${product.slug}`);
  };

  const pickStoreKeyword = (keyword) => {
    setSearchInput(keyword);
    setSuggestOpen(true);
  };

  const handleStoreSearchKeyDown = (e) => {
    const products = searchSuggestions.products || [];
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSuggestOpen(true);
      setSuggestActiveIndex((idx) => (products.length ? (idx + 1) % products.length : -1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSuggestActiveIndex((idx) => (products.length ? (idx <= 0 ? products.length - 1 : idx - 1) : -1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (suggestActiveIndex >= 0 && products[suggestActiveIndex]) {
        pickStoreProduct(products[suggestActiveIndex]);
      } else {
        submitStoreSearch();
      }
    } else if (e.key === "Escape") {
      setSuggestOpen(false);
      setSuggestActiveIndex(-1);
    }
  };

  // If URL has a category (slug or legacy Korean id), show full category page
  if (isCategoryView) {
    return (
      <CategoryFullPage
        products={categoryProducts}
        category={resolvedCategory}
        allCategories={STORE_CATEGORIES}
        loading={loading}
      />
    );
  }

  return (
    <div className="dept-store-page bg-white min-h-screen">
      <SEO
        title={t.store_seoTitle || '복사기 복합기 스토어 | 렌탈·판매 가격비교 - 일광오피스'}
        description={t.store_seoDesc || '복사기, 복합기, 프린터, 토너 소모품 스토어. 사무용 복사기 렌탈·구매 최저가 비교. 전국 무상 출장 유지보수 포함.'}
        keywords="복사기, 복합기, 복사기 렌탈, 복합기 렌탈, 복사기 판매, 복합기 판매, 복사기 가격, 프린터, 토너 소모품, 고속인쇄기, 사무용 복사기"
        path="/store"
      />

      {/* ── Editorial Hero ── */}
      <section
        className="dept-store-hero"
        style={{
          position: "relative",
          background: "var(--ink-1000)",
          color: "white",
          minHeight: "clamp(280px, 50vh, 420px)",
          display: "flex",
          alignItems: "center",
          overflow: "visible",
          zIndex: 20,
          isolation: "isolate",
          marginTop: -56,
          paddingTop: 56,
        }}
      >
        <div
          className="dept-store-hero-decoration"
          style={{
            position: "absolute",
            inset: 0,
            background:
              "radial-gradient(900px 500px at 20% 0%, rgba(0,113,227,0.18), transparent 60%), radial-gradient(700px 400px at 90% 100%, rgba(124,58,237,0.12), transparent 60%)",
            pointerEvents: "none",
          }}
        />
        <div
          className="dept-store-hero-decoration"
          style={{
            position: "absolute",
            inset: 0,
            opacity: 0.04,
            backgroundImage:
              "linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)",
            backgroundSize: "120px 120px",
            pointerEvents: "none",
          }}
        />
        <div
          className="dept-store-hero__content mob-pad-tight"
          style={{
            position: "relative",
            maxWidth: 1240,
            margin: "0 auto",
            padding: "100px 40px 80px",
            width: "100%",
          }}
        >
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "6px 14px",
              background: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.12)",
              borderRadius: 999,
              fontSize: 11,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              fontWeight: 500,
              marginBottom: 24,
            }}
          >
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: "#34d399",
                boxShadow: "0 0 8px #34d399",
              }}
            />
            Store · 스토어
          </div>
          <h1
            style={{
              fontSize: "clamp(44px, 7vw, 96px)",
              lineHeight: 0.98,
              letterSpacing: "-0.04em",
              fontWeight: 700,
              margin: 0,
              color: "white",
            }}
          >
            <span style={{ display: "block" }}>복합기 · 프린터 · 토너</span>
            <span
              style={{
                display: "block",
                fontFamily: "var(--font-serif)",
                fontStyle: "italic",
                fontWeight: 400,
                background: "linear-gradient(135deg, #fff 0%, #6BB1F5 70%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              제품 검색
            </span>
          </h1>
          <p
            style={{
              maxWidth: 520,
              marginTop: 24,
              fontSize: 17,
              lineHeight: 1.55,
              color: "rgba(255,255,255,0.66)",
            }}
          >
            모델명과 토너명으로 검색하고 판매가, 월 렌탈료와 주요 사양을 바로 비교하세요.
          </p>

          {/* Search bar */}
          <form
            ref={storeSearchRef}
            onSubmit={(e) => {
              e.preventDefault();
              submitStoreSearch();
            }}
            style={{ position: "relative", zIndex: 80, maxWidth: 560, marginTop: 36 }}
          >
            <svg
              style={{
                position: "absolute",
                left: 18,
                top: "50%",
                transform: "translateY(-50%)",
                width: 18,
                height: 18,
                color: "rgba(255,255,255,0.5)",
              }}
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
            </svg>
            <input
              type="text"
              placeholder={t.store_searchPlaceholder || '제품명, 브랜드, 모델명 검색'}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onFocus={() => setSuggestOpen(true)}
              onKeyDown={handleStoreSearchKeyDown}
              aria-autocomplete="list"
              style={{
                width: "100%",
                padding: "14px 44px 14px 44px",
                fontSize: 15,
                color: "white",
                background: "rgba(255,255,255,0.08)",
                border: suggestOpen ? "1px solid rgba(112,184,255,0.65)" : "1px solid rgba(255,255,255,0.14)",
                borderRadius: 999,
                outline: "none",
                backdropFilter: "blur(12px)",
                boxShadow: suggestOpen ? "0 0 0 4px rgba(0,113,227,0.16)" : "none",
              }}
            />
            {searchInput && (
              <button
                type="button"
                aria-label="검색어 지우기"
                onClick={() => { setSearchInput(""); setSearchQuery(""); submitStoreSearch(""); }}
                style={{
                  position: "absolute",
                  right: 12,
                  top: "50%",
                  transform: "translateY(-50%)",
                  width: 22,
                  height: 22,
                  borderRadius: "50%",
                  background: "rgba(255,255,255,0.25)",
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <svg width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
            {suggestOpen && (
              <SearchSuggestionsPanel
                query={searchInput}
                suggestions={searchSuggestions}
                activeIndex={suggestActiveIndex}
                onPickProduct={pickStoreProduct}
                onPickKeyword={pickStoreKeyword}
                onSubmit={() => submitStoreSearch()}
                style={{
                  width: "min(560px, calc(100vw - 32px))",
                  left: 0,
                  zIndex: 2200,
                }}
              />
            )}
          </form>
        </div>
      </section>

      {/* ── Loading ── */}
      {loading && !isSearching && (
        <div className="text-center py-24">
          <div className="w-8 h-8 border-2 border-[#0071e3] border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-[15px] text-[#86868b] mt-4">{t.store_loading || '제품을 불러오는 중...'}</p>
        </div>
      )}

      {/* ── Search Mode ── */}
      {isSearching && (
        <>
          {searchLoading || !searchResults ? (
            <div className="text-center py-24">
              <div className="w-8 h-8 border-2 border-[#0071e3] border-t-transparent rounded-full animate-spin mx-auto" />
              <p className="text-[15px] text-[#86868b] mt-4">{t.store_loading || '제품을 불러오는 중...'}</p>
            </div>
          ) : searchResults.length > 0 ? (
            <SearchResultsGrid products={searchResults} query={searchQuery} />
          ) : (
            <div className="text-center py-24">
              <svg className="w-16 h-16 text-[#d2d2d7] mx-auto mb-4" fill="none" stroke="currentColor" strokeWidth={0.8} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
              </svg>
              <p className="text-[17px] text-[#86868b]">{t.store_noResults || '검색 결과가 없습니다.'}</p>
              <p className="text-[13px] text-[#86868b] mt-1">{t.store_tryOther || '다른 키워드로 검색해보세요.'}</p>
            </div>
          )}
        </>
      )}

      {/* ── Browse Mode: Category Grid + Carousels ── */}
      {!loading && !isSearching && (
        <>
          {/* ── Category Grid (Apple Store style) ── */}
          <section className="bg-white py-12 md:py-16">
            <div className="max-w-5xl mx-auto px-6">
              <h2 className="text-[28px] md:text-[36px] font-bold tracking-tight text-[#1d1d1f] text-center">
                {t.store_shopByCategory || '카테고리별로 쇼핑하기'}
              </h2>
              <p className="text-[16px] text-[#6e6e73] text-center mt-2">
                {t.store_shopByCategorySub || '원하는 카테고리를 선택하세요.'}
              </p>

              <div className="grid grid-cols-3 md:grid-cols-6 gap-x-4 gap-y-8 mt-10 max-w-[840px] mx-auto">
                {STORE_CATEGORIES.map((cat) => {
                  const count = categoryCounts[cat.id] ?? (grouped[cat.id]?.length || 0);
                  return (
                    <button
                      key={cat.id}
                      onClick={() => handleCategoryClick(cat.id)}
                      className="flex flex-col items-center gap-2.5 group cursor-pointer"
                    >
                      <div className="w-20 h-20 md:w-24 md:h-24 rounded-2xl bg-gray-50 group-hover:bg-gray-100 transition-colors flex items-center justify-center overflow-hidden">
                        <img
                          src={cat.img}
                          alt={cat.label}
                          className="object-contain group-hover:scale-105 transition-transform duration-300 w-12 h-12 md:w-16 md:h-16"
                          loading="lazy"
                          onError={(event) => showCategoryFallback(event, cat.fallback)}
                        />
                      </div>
                      <span className="text-[14px] md:text-[15px] font-medium text-[#1d1d1f] group-hover:text-[#0071e3] transition-colors text-center leading-snug">
                        {t[cat.tKey] || cat.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </section>

          {/* ── Promo Banners (렌탈 / B2B) ── */}
          <section className="max-w-5xl mx-auto px-6 py-6 md:py-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Link to="/rental" className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-600 via-violet-600 to-purple-700 p-6 md:p-8 group hover:shadow-xl transition-all duration-500">
                <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_70%_30%,rgba(255,255,255,0.2),transparent_60%)]" />
                <div className="relative z-10">
                  <span className="inline-block text-[10px] font-bold tracking-widest text-white/70 uppercase mb-2">RENTAL</span>
                  <h3 className="text-xl md:text-2xl font-bold text-white tracking-tight mb-1">전국 렌탈</h3>
                  <p className="text-[13px] text-white/80 leading-relaxed mb-4">월 렌탈료로 복합기를 부담 없이.<br className="hidden md:block" />전국 어디서나 설치·유지보수까지.</p>
                  <span className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-white group-hover:gap-2.5 transition-all duration-300">
                    렌탈 상담받기
                    <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" /></svg>
                  </span>
                </div>
              </Link>
              <Link to="/b2b" className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-700 p-6 md:p-8 group hover:shadow-xl transition-all duration-500">
                <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_30%_70%,rgba(255,255,255,0.2),transparent_60%)]" />
                <div className="relative z-10">
                  <span className="inline-block text-[10px] font-bold tracking-widest text-white/70 uppercase mb-2">B2B</span>
                  <h3 className="text-xl md:text-2xl font-bold text-white tracking-tight mb-1">사업자몰</h3>
                  <p className="text-[13px] text-white/80 leading-relaxed mb-4">사업자 전용 특가 혜택.<br className="hidden md:block" />대량 구매 할인 · 세금계산서 발행.</p>
                  <span className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-white group-hover:gap-2.5 transition-all duration-300">
                    사업자몰 바로가기
                    <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" /></svg>
                  </span>
                </div>
              </Link>
            </div>
          </section>

          {/* ── Category Carousels ── */}
          {STORE_CATEGORIES.map((cat) => {
            const items = grouped[cat.id] || [];
            if (items.length === 0) return null;
            return (
              <section
                key={cat.id}
                id={`section-${cat.id}`}
                className="border-t border-[#d2d2d7]/40"
                style={{ scrollMarginTop: "56px" }}
              >
                <ProductCarousel products={items} title={t[cat.tKey] || cat.label} categoryId={cat.id} />
              </section>
            );
          })}
        </>
      )}
    </div>
  );
}
